import EncryptedStorage from 'react-native-encrypted-storage';
import { Platform } from 'react-native';
import type { MessageKey } from '../../i18n/messages';

/**
 * Черновики по чатам (аудит удобства 2026-10-04, §3 #20).
 *
 * <p>Раньше набранное жило только в состоянии экрана: вышел из чата посмотреть адрес — и текст
 * пропал. Теперь у каждого чата свой черновик, он переживает уход с экрана и перезапуск, а список
 * чатов может показать «Черновик: …» в строке чата.
 *
 * <p><b>Для списка чатов.</b> {@link getDraft} отдаёт черновик синхронно из памяти (после
 * {@link hydrateDrafts}), {@link subscribeDrafts} сообщает о каждой смене — строке этого
 * достаточно, чтобы показать превью и обновить его, не дожидаясь возврата на экран.
 *
 * <p>Хранение то же, что у прочих настроек экрана (`savedSectionPrefsStorage`): защищённое
 * хранилище на телефоне, `localStorage` в вебе; ошибки глотаются — черновик это удобство, и
 * уронить из-за него экран нельзя.
 */

const KEY = 'vr_chat_drafts_v1';

/** Столько чатов с черновиками помним; самые давние вытесняются. */
export const MAX_DRAFT_CHATS = 200;
/** Потолок длины: тот же, что у поля ввода, с запасом на маркеры смайлов. */
export const MAX_DRAFT_CHARS = 8_000;
/** Пауза перед записью на диск: набор текста не должен писать в хранилище на каждую букву. */
const PERSIST_DEBOUNCE_MS = 400;

type DraftRecord = { text: string; at: number };

let drafts: Record<string, DraftRecord> = {};
let hydrated = false;
let hydrating: Promise<void> | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<(chatId: string, text: string) => void>();

function isWeb(): boolean {
  return Platform.OS === 'web';
}

function normalize(raw: unknown): Record<string, DraftRecord> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, DraftRecord> = {};
  for (const [chatId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!chatId || !value || typeof value !== 'object') continue;
    const rec = value as { text?: unknown; at?: unknown };
    if (typeof rec.text !== 'string' || rec.text.trim().length === 0) continue;
    const at = typeof rec.at === 'number' && Number.isFinite(rec.at) ? rec.at : 0;
    out[chatId] = { text: rec.text.slice(0, MAX_DRAFT_CHARS), at };
  }
  return out;
}

/** Прочитать черновики с диска один раз; повторные вызовы ждут тот же самый запрос. */
export function hydrateDrafts(): Promise<void> {
  if (hydrated) return Promise.resolve();
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const raw = isWeb() ? globalThis.localStorage?.getItem(KEY) ?? null : await EncryptedStorage.getItem(KEY);
      const fromDisk = raw ? normalize(JSON.parse(raw)) : {};
      // То, что успели набрать до конца чтения, новее диска.
      drafts = { ...fromDisk, ...drafts };
    } catch {
      /* битая запись — начинаем с чистого листа */
    } finally {
      hydrated = true;
      hydrating = null;
    }
  })();
  return hydrating;
}

/** Черновик чата из памяти; пустая строка — черновика нет (или ещё не прочитан с диска). */
export function getDraft(chatId: string): string {
  return drafts[chatId]?.text ?? '';
}

/** Черновик чата, дождавшись чтения с диска. */
export async function loadDraft(chatId: string): Promise<string> {
  await hydrateDrafts();
  return getDraft(chatId);
}

/** Все черновики — для списка чатов, которому нужны сразу все строки. */
export function getAllDrafts(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [chatId, rec] of Object.entries(drafts)) out[chatId] = rec.text;
  return out;
}

/**
 * Записать черновик. Пустой (или из одних пробелов) — удаляет его: отправленное сообщение и
 * стёртое поле черновиком не считаются.
 */
export function setDraft(chatId: string, text: string, now = Date.now()): void {
  if (!chatId) return;
  const clean = text.trim().length === 0 ? '' : text.slice(0, MAX_DRAFT_CHARS);
  const prev = drafts[chatId]?.text ?? '';
  if (prev === clean) return;
  if (clean) {
    drafts = { ...drafts, [chatId]: { text: clean, at: now } };
    trimToLimit();
  } else {
    const next = { ...drafts };
    delete next[chatId];
    drafts = next;
  }
  for (const l of listeners) {
    try {
      l(chatId, clean);
    } catch {
      /* чужой подписчик не должен ронять запись черновика */
    }
  }
  schedulePersist();
}

/**
 * Строка превью в списке чатов: «Черновик: текст» одной строкой. Для `ChatListScreen` — там
 * черновик должен перебивать последнее сообщение, как в Telegram: иначе о недописанном
 * человек узнаёт, только открыв чат.
 */
export function draftPreviewLine(draft: string, t: (key: MessageKey) => string): string {
  const oneLine = draft.replace(/\s+/g, ' ').trim();
  return `${t('chatDraftPrefix')}: ${oneLine.length > 120 ? `${oneLine.slice(0, 120)}…` : oneLine}`;
}

/** Подписка на смену черновиков; возвращает отписку. */
export function subscribeDrafts(listener: (chatId: string, text: string) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function trimToLimit(): void {
  const ids = Object.keys(drafts);
  if (ids.length <= MAX_DRAFT_CHATS) return;
  const keep = ids.sort((a, b) => drafts[b].at - drafts[a].at).slice(0, MAX_DRAFT_CHATS);
  const next: Record<string, DraftRecord> = {};
  for (const id of keep) next[id] = drafts[id];
  drafts = next;
}

function schedulePersist(): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    void persistNow();
  }, PERSIST_DEBOUNCE_MS);
}

/** Записать немедленно — на уходе с экрана и в тестах. */
export async function persistNow(): Promise<void> {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  const payload = JSON.stringify(drafts);
  try {
    if (isWeb()) {
      globalThis.localStorage?.setItem(KEY, payload);
      return;
    }
    await EncryptedStorage.setItem(KEY, payload);
  } catch {
    /* ignore storage write errors */
  }
}

/** Только для тестов. */
export function resetDraftsForTests(): void {
  drafts = {};
  hydrated = false;
  hydrating = null;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = null;
  listeners.clear();
}
